import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AIPresetsManager } from "@/components/AIPresetsManager";
import { Icon } from "@/components/Icon";
import { TgEmoji } from "@/components/TgEmoji";
import { tg } from "@/lib/telegram";
import { showAppAlert } from "@/lib/appAlert";
import { openTelegramUserLink } from "@/lib/openExternal";
import { isLikelyUrl } from "@/lib/utils";
import { useTopInset } from "@/lib/useTopInset";
import { VoiceBioButton } from "@/components/VoiceBioButton";
import {
  useProfileFull,
  useUpdateProfileFull,
  useGenerateAIResponse,
} from "@/api/hooks";
import { ApiError } from "@/api/client";

/**
 * AIResponseSheet — генерація відгуку на вакансію (юзер 2026-06-14).
 *
 * Флоу: профіль не заповнений → форма «Про тебе» прямо тут (зберіг → одразу
 * генерує, інфо лягає й у профіль) → вибір стилю → відгук + «Копіювати» /
 * «Згенерувати ще» (той самий стиль, кардинально інакше) / «Копіювати й
 * відкрити чат». Доступ/ліміти гейтить бекенд (FREE — 402, тест — 5/добу).
 */

function copyText(text: string) {
  try {
    navigator.clipboard?.writeText(text);
  } catch {
    /* ignore — clipboard may be unavailable */
  }
}


/** Мова відгуку. Промт може бути написаний будь-якою мовою — відповідь
 *  пишеться тією, яку обрала людина (owner 2026-09-16). */
type AiLang = "uk" | "ru" | "en";

const LANG_KEY = "ai_reply_lang";
const LANG_LABEL: Record<AiLang, string> = {
  uk: "🇺🇦 Укр",
  en: "🇺🇸 Eng",
  ru: "🇷🇺 Рус",
};

function readLang(): AiLang {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === "uk" || v === "ru" || v === "en") return v;
  } catch {
    /* ignore */
  }
  return "uk";
}

export function AIResponseSheet({
  vacancyId,
  contactUsername,
  onClose,
  openStyleForm = false,
}: {
  vacancyId: number;
  contactUsername?: string | null;
  onClose: () => void;
  /** Відкрити одразу форму власного стилю — з кнопки в боті
   *  «Створити свій стиль 💫» (owner 2026-08-31). */
  openStyleForm?: boolean;
}) {
  const profileQ = useProfileFull();
  const updateMut = useUpdateProfileFull();
  const genMut = useGenerateAIResponse();
  // Safe-area зверху (overlay-кнопки Telegram «Закрити/Згорнути»). Додаємо
  // paddingTop контейнеру → центрована модалка НЕ заходить під ці кнопки,
  // навіть коли клавіатура піджимає її вгору (юзер 2026-06-25).
  const topInset = useTopInset();

  const p = profileQ.data;
  const minChars = p?.bio_min_chars ?? 300;
  const maxChars = p?.bio_max_chars ?? 2000;
  const bioFilled =
    !!p &&
    ((p.bio?.trim().length ?? 0) >= minChars ||
      (!!p.full_name && !!p.specialty));
  // Owner 2026-09-15: портфоліо БІЛЬШЕ не обов'язкове — «я б посилання не робив
  // обов'язковим, але якщо воно є — можна використовувати». Для генерації
  // достатньо «Про тебе»; посилання, якщо воно є, іде в промт.
  const profileFilled = bioFilled;

  const [bio, setBio] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [presetId, setPresetId] = useState<number | null>(null);
  // Мова відгуку (owner 2026-09-16). Запамʼятовуємо вибір: людина зазвичай
  // пише тією ж мовою, тому щоразу тицяти не треба.
  const [lang, setLangRaw] = useState<AiLang>(() => readLang());
  const setLang = (v: AiLang) => {
    setLangRaw(v);
    try {
      localStorage.setItem(LANG_KEY, v);
    } catch {
      /* приватний режим — просто не запамʼятаємо */
    }
  };
  // Чи відкрита форма промпта — щоб не малювати хрестик поруч зі «Скасувати».
  const [formOpen, setFormOpen] = useState(openStyleForm);
  const [text, setText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Форма додавання кастомного стилю

  // Префіл існуючим bio + посиланням на портфоліо.
  useEffect(() => {
    if (p?.bio) setBio(p.bio);
  }, [p?.bio]);
  useEffect(() => {
    if (p?.portfolio_url) setPortfolioUrl(p.portfolio_url);
  }, [p?.portfolio_url]);

  // Лок скролу фону поки шторка відкрита.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const runGenerate = (pid: number, regenerate = false) => {
    setPresetId(pid);
    setCopied(false);
    tg()?.HapticFeedback?.impactOccurred?.("light");
    genMut.mutate(
      {
        vacancy_id: vacancyId,
        preset_id: pid,
        lang,
        regenerate,
        // При «Згенерувати ще» передаємо попередній варіант → новий буде інший.
        previous_text: regenerate ? text : null,
      },
      {
        onSuccess: (r) => {
          setText(r.response_text);
          tg()?.HapticFeedback?.notificationOccurred?.("success");
        },
        onError: (e) => {
          const err = e as ApiError;
          tg()?.HapticFeedback?.notificationOccurred?.("error");
          const msg = err?.message || "Спробуй пізніше";
          if (err?.status === 402) {
            showAppAlert(msg, { title: "Потрібна підписка", emoji: "🔒" });
          } else if (err?.status === 429) {
            showAppAlert(msg, { title: "Ліміт генерацій", emoji: "⏳" });
          } else if (err?.status === 400) {
            showAppAlert(msg, { title: "Заповни профіль", emoji: "📝" });
          } else {
            showAppAlert(msg, { title: "Не вдалося згенерувати", emoji: "⚠️" });
          }
        },
      },
    );
  };

  const saveProfile = () => {
    const bt = bio.trim();
    const pu = portfolioUrl.trim();
    // Bio: якщо ще не заповнено — вимагаємо мінімум; якщо вже заповнено
    // (в т.ч. legacy) — bio у пейлоад не чіпаємо, щоб не стерти.
    const payload: import("@/api/hooks").ProfileFullUpdateIn = {};
    if (bt.length >= minChars) {
      payload.bio = bt;
    } else if (!bioFilled) {
      showAppAlert(`Додай мінімум ${minChars} символів про себе.`, {
        title: "Замало тексту",
      });
      return;
    }
    // Посилання необов'язкове: порожнє — просто не зберігаємо.
    if (pu && !isLikelyUrl(pu)) {
      showAppAlert(
        "Схоже, це не посилання. Встав робоче посилання на портфоліо (напр. behance.net/…, t.me/…).",
        { title: "Невірне посилання" },
      );
      return;
    }
    if (pu) payload.portfolio_url = pu;
    updateMut.mutate(payload, {
      onSuccess: () => tg()?.HapticFeedback?.notificationOccurred?.("success"),
      onError: (e) =>
        showAppAlert((e as Error).message, { title: "Помилка збереження" }),
    });
  };



  // Переспитати перед видаленням (юзер 2026-06-25). Емодзі 😔 — анімований
  // (Lottie-набір), бо над заголовком попапа емодзі має бути анімованим.



  const doCopy = (t: string) => {
    copyText(t);
    tg()?.HapticFeedback?.notificationOccurred?.("success");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // --- Тіло шторки ---
  let body: React.ReactNode;
  if (profileQ.isLoading) {
    body = (
      <div className="py-10 text-center text-[0.8125rem] text-ink-dim">Завантажую…</div>
    );
  } else if (!profileFilled) {
    // Форма «Про тебе» + посилання на портфоліо (показуємо лише те, чого бракує)
    const len = bio.trim().length;
    const bioOk = bioFilled || (len >= minChars && len <= maxChars);
    const portOk = portfolioUrl.trim().length > 0;
    const ok = bioOk && portOk;
    body = (
      <div>
        <p className="mb-3 text-[0.8125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
          Щоб AI написав влучний відгук, розкажи про себе: спеціалізація,
          досвід, сильні сторони. Посилання на портфоліо — якщо воно є: тоді AI
          зможе його згадати у відгуку. Усе збережеться в профілі.
        </p>

        {bioFilled ? (
          <div className="mb-3 flex items-center gap-2 rounded-xl bg-success/10 px-3 py-2.5 text-[0.8125rem] font-semibold text-success">
            ✓ «Про тебе» заповнено
          </div>
        ) : (
          <>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={maxChars}
              rows={6}
              placeholder="Напр.: SMM-спеціаліст, 3 роки досвіду. Веду Instagram і TikTok брендів, спеціалізуюсь на reels та контент-стратегії…"
              className="w-full bg-bg rounded-xl p-3 text-[0.875rem] leading-relaxed text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
            <div className={"mt-1 text-[0.6875rem] " + (len >= minChars || len === 0 ? "text-ink-dim" : "text-red-500")}>
              {len} / {maxChars} · мінімум {minChars}
            </div>
            <div className="mt-3">
              <VoiceBioButton
                onResult={(t) =>
                  setBio((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${t}` : t))
                }
              />
            </div>
          </>
        )}

        {/* Посилання на портфоліо — НЕобов'язкове (owner 2026-09-15). */}
        <div className="mt-3">
          <div className="mb-1.5 text-[0.78125rem] font-semibold text-ink">
            Посилання на портфоліо{" "}
            <span className="font-normal text-ink-faint">— якщо є</span>
          </div>
          <input
            type="url"
            inputMode="url"
            value={portfolioUrl}
            onChange={(e) => setPortfolioUrl(e.target.value)}
            placeholder="https://behance.net/… або t.me/…"
            className="w-full bg-bg rounded-xl p-3 text-[0.875rem] text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          <div className="mt-1 text-[0.6875rem] text-ink-dim">
            AI ненав'язливо додасть це посилання у відгук.
          </div>
        </div>

        <button
          type="button"
          disabled={!ok || updateMut.isPending}
          onClick={saveProfile}
          className={
            "mt-4 block w-full rounded-full py-3 font-display text-[0.84375rem] font-bold transition " +
            (!ok || updateMut.isPending
              ? "bg-ink/10 text-ink-dim cursor-not-allowed"
              : "bg-primary text-white active:bg-primary-deep")
          }
        >
          {updateMut.isPending ? "Зберігаю…" : "Зберегти й продовжити"}
        </button>
      </div>
    );
  } else if (genMut.isPending) {
    body = (
      <div className="py-10 text-center">
        <div className="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
        <div className="text-[0.8125rem] text-ink-dim">AI пише відгук…</div>
      </div>
    );
  } else if (text) {
    body = (
      <div>
        <div className="rounded-xl bg-bg/60 border border-ink/[0.06] p-3.5 text-[0.875rem] leading-relaxed text-ink whitespace-pre-wrap [overflow-wrap:anywhere]">
          {text}
        </div>
        <div className="mt-3 space-y-2">
          <button
            type="button"
            onClick={() => doCopy(text)}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 font-display text-[0.875rem] font-bold text-white active:bg-primary-deep transition"
          >
            {copied ? "Скопійовано ✓" : "Копіювати"}
          </button>
          {contactUsername && (
            <button
              type="button"
              onClick={() => {
                doCopy(text);
                const handle = (contactUsername || "").replace(/^@+/, "").trim();
                // openTelegramUserLink чекає t.me-лінк, не голий username —
                // інакше openLink відкриває його як домен у браузері
                // (юзер 2026-06-14). Будуємо повний t.me-URL.
                if (handle) openTelegramUserLink(`https://t.me/${handle}`);
              }}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-surface-soft border border-ink/15 py-3 font-display text-[0.875rem] font-semibold text-ink active:scale-[0.98] transition"
            >
              <Icon name="chat" size={16} color="#FE6B04" />
              Копіювати й відкрити чат
            </button>
          )}
          <button
            type="button"
            onClick={() => presetId && runGenerate(presetId, true)}
            className="block w-full rounded-full bg-transparent py-2.5 font-display text-[0.8125rem] text-ink-dim active:bg-ink/5 transition"
          >
            Згенерувати ще
          </button>
        </div>
      </div>
    );
  } else {
    // Список промптів і форма — спільний компонент із профілем
    // (owner 2026-09-12: «щоб можна було налаштувати і в профілі»).
    body = (
      <div>
        <p className="mb-3 text-[0.8125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
          Відгук пишеться строго за промптом. Натисни на назву, щоб прочитати
          або змінити промпт, або одразу генеруй.
        </p>

        {/* Мова відгуку (owner 2026-09-16): три кнопки над промптами. Вибір
            запамʼятовується, бо люди зазвичай пишуть однією мовою. */}
        <div className="mb-3 flex items-center gap-2">
          <span className="text-[0.6875rem] uppercase tracking-wide text-ink-faint">
            Мова
          </span>
          <div className="flex flex-1 gap-1 rounded-full bg-bg p-1">
            {(["uk", "en", "ru"] as AiLang[]).map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => {
                  tg()?.HapticFeedback?.selectionChanged?.();
                  setLang(code);
                }}
                className={
                  "flex-1 rounded-full py-1.5 font-display text-[0.75rem] font-semibold transition " +
                  (lang === code
                    ? "bg-surface text-ink shadow-sm"
                    : "text-ink-faint active:bg-ink/5")
                }
              >
                {LANG_LABEL[code]}
              </button>
            ))}
          </div>
        </div>

        <AIPresetsManager
          onGenerate={(id) => runGenerate(id)}
          initialForm={openStyleForm}
          onFormOpenChange={setFormOpen}
        />
      </div>
    );
  }

  return createPortal(
    // Центровано (юзер 2026-06-25: верхній анкор перекривався системними
    // кнопками Telegram). Якщо клавіатура трохи зсуває — це ок, головне центр.
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      style={{ paddingTop: topInset + 16 }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-surface rounded-3xl p-5 shadow-2xl overflow-y-auto"
        style={{ maxHeight: `calc(88vh - ${topInset}px)` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center gap-2">
          <TgEmoji char="🤖" size={24} />
          <div className="flex-1 font-display text-[1rem] font-bold text-ink">
            Відповідь на вакансію
          </div>
          {/* Хрестик прибираємо, коли у формі вже є «Скасувати» — кнопки не
              дублюють одна одну (owner 2026-09-12). `-mr-1.5` ставить глиф на
              ту саму праву лінію, що й краї карток нижче. */}
          {!formOpen && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Закрити"
              className="-mr-1.5 grid h-8 w-8 place-items-center rounded-full text-ink-dim active:bg-ink/5"
            >
              <Icon name="plus" size={16} className="rotate-45" />
            </button>
          )}
        </div>
        {body}
      </div>
    </div>,
    document.body,
  );
}
